/**
 * useImageGeneration - Hook for managing image generation state and operations
 */

import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { ImageGenStatus } from "@optidev/shared-types";
import { auth } from "@/lib/firebase";
import {
  MAX_REFERENCE_IMAGES,
  GeneratingImage,
  ImageGenRequestItem,
  ReferenceImage,
} from "@/types/image-generate-panel.types";

interface UseImageGenerationProps {
  projectId?: string;
  devContainerId?: string; // For local save mode (skip R2, save directly to dev-container)
  previewUrl?: string; // For generating downloadUrl for local assets
}

interface UseImageGenerationReturn {
  // Gallery state
  generatedImages: ImageGenRequestItem[];
  isLoadingAssets: boolean;
  loadGeneratedImages: () => Promise<void>;

  // Selection state
  selectedAssets: Set<string>;
  toggleSelection: (refId: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  isDeleting: boolean;
  handleDeleteSelected: () => Promise<void>;

  // Editor state
  editingImage: ImageGenRequestItem | null;
  isEdit: boolean;
  isEditorOpen: boolean;
  openImageEditor: (img: ImageGenRequestItem) => void;
  closeImageEditor: () => void;
  handleImageUpdated: () => void;

  // Generation state
  generatingImages: GeneratingImage[];
  isGenerating: boolean;
  prompt: string;
  setPrompt: (value: string) => void;
  filename: string;
  setFilename: (value: string) => void;
  aspectRatio: string;
  setAspectRatio: (value: string) => void;
  stylePreset: string;
  setStylePreset: (value: string) => void;
  handleGenerate: () => Promise<void>;
  removeGeneratingImage: (requestId: string) => void;

  // Reference images
  referenceImages: ReferenceImage[];
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleReferenceImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeReferenceImage: (index: number) => void;

  // Computed values
  selectedImagesForEdit: ImageGenRequestItem[];
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

/**
 * Strip token query parameters from URL
 * Removes ?tkn=... or &tkn=... from URLs (used for HMAC bootstrap tokens)
 */
function stripTokenFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("tkn");
    urlObj.searchParams.delete("token");
    return urlObj.toString();
  } catch {
    // If URL parsing fails, try simple regex replacement
    return url.replace(/[?&]tkn=[^&]+/, "").replace(/[?&]token=[^&]+/, "");
  }
}

export function useImageGeneration({
  projectId,
  devContainerId,
  previewUrl,
}: UseImageGenerationProps): UseImageGenerationReturn {
  // Generated images from API
  const [generatedImages, setGeneratedImages] = useState<ImageGenRequestItem[]>(
    [],
  );
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // Selection state for bulk actions
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Image editor state
  const [editingImage, setEditingImage] = useState<ImageGenRequestItem | null>(
    null,
  );
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // Image generation state
  const [prompt, setPrompt] = useState("");
  const [filename, setFilename] = useState("");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [stylePreset, setStylePreset] = useState("none");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingImages, setGeneratingImages] = useState<GeneratingImage[]>(
    [],
  );

  // Reference images upload state
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ref to track if component is mounted for polling cleanup
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      referenceImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, []);

  // Get selected images for display/editing
  const selectedImagesForEdit = generatedImages.filter(
    (img) =>
      selectedAssets.has(img.id) &&
      img.status === "completed" &&
      img.downloadUrl,
  );

  // Load local assets from dev-container
  const loadGeneratedImages = useCallback(async () => {
    if (!devContainerId) return;

    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/image-generation/assets/${devContainerId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.files) {
        // Convert local asset files to ImageGenRequestItem format
        // Strip token from previewUrl to get clean base URL for asset paths
        const baseUrl = previewUrl
          ? stripTokenFromUrl(previewUrl).replace(/\/$/, "")
          : "";
        // Get directory from response (set by API's _destinationDir constant)
        // and derive relative path for URL construction
        const directory = response.data.directory as string;
        const relativePath = directory
          .replace(/^\/workspaces\/public/, "")
          .replace(/\/$/, ""); // Remove trailing slash
        const localImages: ImageGenRequestItem[] = response.data.files.map(
          (file: {
            name: string;
            path: string;
            assetPath?: string;
            size: number;
            mimeType: string;
            createdAt: string;
            modifiedAt: string;
          }) => {
            // Use assetPath from response if available, otherwise construct from directory
            // Normalize path to avoid double slashes (e.g., /assets/img-gen//file.png)
            const rawAssetPath =
              file.assetPath || `${relativePath}/${file.name}`;
            const assetPath = rawAssetPath.replace(/\/+/g, "/");

            // Add cache-busting parameter using modifiedAt timestamp to ensure fresh images
            const cacheBuster = new Date(file.modifiedAt).getTime();
            const urlWithCache = baseUrl
              ? `${baseUrl}${assetPath}?t=${cacheBuster}`
              : undefined;

            return {
              id: `local-${file.name}`,
              status: "completed" as const,
              prompt: `Local asset: ${file.name}`,
              expectedFilename: file.name,
              aspectRatio: "1:1",
              // Construct downloadUrl from previewUrl + assetPath (for preview display)
              downloadUrl: urlWithCache,
              // Use assetPath for drag-and-drop (used in InsertPanel)
              assetPath,
              createdAt: file.createdAt,
              completedAt: file.modifiedAt,
            };
          },
        );
        setGeneratedImages(localImages);
      } else {
        setGeneratedImages([]);
      }
    } catch (error) {
      console.error("Failed to load local assets:", error);
      // Don't show error toast - assets folder might not exist yet
      setGeneratedImages([]);
    }
  }, [devContainerId, previewUrl]);

  // Load assets on mount
  const loadAssets = useCallback(async () => {
    if (!devContainerId) return;

    setIsLoadingAssets(true);
    try {
      await loadGeneratedImages();
    } finally {
      setIsLoadingAssets(false);
    }
  }, [devContainerId, loadGeneratedImages]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  // Generate filename from prompt
  const generateFilename = useCallback((promptText: string) => {
    if (!promptText.trim()) return "";
    const slug = promptText
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 30);
    return `${slug}-${Date.now().toString(36)}.png`;
  }, []);

  // Poll for image generation status
  const pollImageStatus = useCallback(
    async (requestId: string) => {
      const token = await auth.getIdToken();
      if (!token) return;

      const maxAttempts = 60;
      let attempts = 0;

      const poll = async () => {
        if (!isMountedRef.current) return;

        try {
          const response = await axios.get(
            `${API_URL}/api/image-generation/status/${requestId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (!isMountedRef.current) return;

          const {
            status,
            downloadUrl,
            savedLocally,
            resultFileId,
            errorMessage,
          } = response.data;

          setGeneratingImages((prev) =>
            prev.map((img) =>
              img.requestId === requestId
                ? { ...img, status, downloadUrl, resultFileId, errorMessage }
                : img,
            ),
          );

          // Handle completion - either with downloadUrl (R2) or savedLocally (dev-container)
          if (status === "completed" && (downloadUrl || savedLocally)) {
            toast.success("Image generated successfully!");

            // Add small delay before reloading to ensure file is fully written and accessible
            setTimeout(() => {
              if (isMountedRef.current) {
                loadGeneratedImages();
              }
            }, 500);

            setTimeout(() => {
              if (isMountedRef.current) {
                setGeneratingImages((prev) =>
                  prev.filter((img) => img.requestId !== requestId),
                );
              }
            }, 3000);
            return;
          }

          if (status === "failed") {
            toast.error(errorMessage || "Image generation failed");
            return;
          }

          attempts++;
          if (attempts < maxAttempts && isMountedRef.current) {
            setTimeout(poll, 2000);
          } else if (attempts >= maxAttempts && isMountedRef.current) {
            toast.error("Image generation timed out");
            setGeneratingImages((prev) =>
              prev.map((img) =>
                img.requestId === requestId
                  ? {
                      ...img,
                      status: ImageGenStatus.FAILED,
                      errorMessage: "Timed out",
                    }
                  : img,
              ),
            );
          }
        } catch (error) {
          console.error("Error polling status:", error);
          attempts++;
          if (attempts < maxAttempts && isMountedRef.current) {
            setTimeout(poll, 2000);
          }
        }
      };

      poll();
    },
    [loadGeneratedImages],
  );

  // Convert File to base64
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Handle image generation
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    if (!projectId) {
      toast.error("Project ID is required");
      return;
    }

    const token = await auth.getIdToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    setIsGenerating(true);

    const finalFilename = filename.trim() || generateFilename(prompt);
    let finalPrompt = prompt.trim();
    if (stylePreset !== "none") {
      finalPrompt = `${finalPrompt}, ${stylePreset} style`;
    }

    try {
      let referenceUrls: string[] = [];
      if (referenceImages.length > 0) {
        referenceUrls = await Promise.all(
          referenceImages.map((img) => fileToBase64(img.file)),
        );
      }

      if (selectedImagesForEdit.length > 0) {
        const selectedUrls = selectedImagesForEdit
          .slice(0, 3)
          .map((img) => img.downloadUrl!)
          .filter(Boolean);
        referenceUrls = [...selectedUrls, ...referenceUrls].slice(0, 3);
      }

      const response = await axios.post(
        `${API_URL}/api/image-generation/generate`,
        {
          projectId,
          prompt: finalPrompt,
          expectedFilename: finalFilename,
          aspectRatio,
          ...(referenceUrls.length > 0 && { referenceUrls }),
          // Include devContainerId for local save mode
          ...(devContainerId && { devContainerId }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const { id: requestId, status } = response.data;

      const newGeneratingImage: GeneratingImage = {
        requestId,
        status,
        prompt: finalPrompt,
        filename: finalFilename,
      };

      setGeneratingImages((prev) => [newGeneratingImage, ...prev]);

      setPrompt("");
      setFilename("");
      referenceImages.forEach((img) => URL.revokeObjectURL(img.preview));
      setReferenceImages([]);
      if (selectedImagesForEdit.length > 0) {
        setSelectedAssets(new Set());
      }

      toast.success("Image generation started!");
      pollImageStatus(requestId);
    } catch (error: unknown) {
      console.error("Generation failed:", error);
      let errorMessage = "Failed to start image generation";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  }, [
    prompt,
    filename,
    aspectRatio,
    stylePreset,
    projectId,
    devContainerId,
    generateFilename,
    pollImageStatus,
    referenceImages,
    fileToBase64,
    selectedImagesForEdit,
  ]);

  // Remove generating image
  const removeGeneratingImage = useCallback((requestId: string) => {
    setGeneratingImages((prev) =>
      prev.filter((img) => img.requestId !== requestId),
    );
  }, []);

  // Toggle selection
  const toggleSelection = useCallback((refId: string) => {
    setSelectedAssets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(refId)) {
        newSet.delete(refId);
      } else {
        newSet.add(refId);
      }
      return newSet;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedAssets(new Set());
  }, []);

  // Select all images
  const selectAll = useCallback(() => {
    const allIds = new Set(generatedImages.map((img) => img.id));
    setSelectedAssets(allIds);
  }, [generatedImages]);

  // Open/close image editor
  const openImageEditor = useCallback(
    (img: ImageGenRequestItem) => {
      clearSelection(); // Clear multi-selection
      setEditingImage(img);
      setIsEdit(true); // Show edit view
    },
    [clearSelection],
  );

  const closeImageEditor = useCallback(() => {
    setIsEdit(false); // Back to generate view
    setEditingImage(null);
  }, []);

  const handleImageUpdated = useCallback(() => {
    loadGeneratedImages();
  }, [loadGeneratedImages]);

  // Handle reference image upload
  const handleReferenceImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const remainingSlots = MAX_REFERENCE_IMAGES - referenceImages.length;

      if (remainingSlots <= 0) {
        toast.error(`Maximum ${MAX_REFERENCE_IMAGES} reference images allowed`);
        return;
      }

      const validFiles: ReferenceImage[] = [];
      const filesToAdd = Array.from(files).slice(0, remainingSlots);

      for (const file of filesToAdd) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} must be less than 10MB`);
          continue;
        }

        validFiles.push({ file, preview: URL.createObjectURL(file) });
      }

      if (validFiles.length > 0) {
        setReferenceImages((prev) => [...prev, ...validFiles]);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [referenceImages.length],
  );

  // Remove reference image
  const removeReferenceImage = useCallback((index: number) => {
    setReferenceImages((prev) => {
      const imageToRemove = prev[index];
      if (imageToRemove?.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Delete selected local images
  const handleDeleteSelected = useCallback(async () => {
    if (selectedAssets.size === 0) return;

    if (!devContainerId) {
      toast.error("Dev container not available");
      return;
    }

    const token = await auth.getIdToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    setIsDeleting(true);

    try {
      const ids = Array.from(selectedAssets);

      // Extract filenames from ids (format: "local-{filename}")
      const filenames = ids.map((id) => id.replace("local-", ""));

      // Bulk delete via new API endpoint
      const response = await axios.post(
        `${API_URL}/api/image-generation/assets/${devContainerId}/delete`,
        { filenames },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const { deleted, failed } = response.data;

      if (deleted > 0) {
        toast.success(`Deleted ${deleted} image${deleted > 1 ? "s" : ""}`);
        setGeneratedImages((prev) =>
          prev.filter((img) => !selectedAssets.has(img.id)),
        );
        setSelectedAssets(new Set());
      }

      if (failed > 0) {
        toast.error(`Failed to delete ${failed} image(s)`);
      }
    } catch (error: unknown) {
      console.error("Delete failed:", error);
      let errorMessage = "Failed to delete images";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedAssets, devContainerId]);

  return {
    // Gallery state
    generatedImages,
    isLoadingAssets,
    loadGeneratedImages,

    // Selection state
    selectedAssets,
    toggleSelection,
    clearSelection,
    selectAll,
    isDeleting,
    handleDeleteSelected,

    // Editor state
    editingImage,
    isEdit,
    isEditorOpen,
    openImageEditor,
    closeImageEditor,
    handleImageUpdated,

    // Generation state
    generatingImages,
    isGenerating,
    prompt,
    setPrompt,
    filename,
    setFilename,
    aspectRatio,
    setAspectRatio,
    stylePreset,
    setStylePreset,
    handleGenerate,
    removeGeneratingImage,

    // Reference images
    referenceImages,
    fileInputRef,
    handleReferenceImageUpload,
    removeReferenceImage,

    // Computed values
    selectedImagesForEdit,
  };
}
